import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "../../components/layout/Layout";
import apiConfig from "../../config/apiConfig";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Check, ArrowRight, HelpCircle } from "lucide-react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    left: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    right: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 40px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const BillingToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 6px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"};
  }
`;

const ToggleButton = styled.button`
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: ${({ $active, theme }) =>
    $active
      ? theme.colors.light?.card?.background || theme.colors.light?.background || "white"
      : "transparent"};
  color: ${({ $active, theme }) =>
    $active
      ? theme.colors.light?.foreground || theme.colors.text.heading
      : theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  box-shadow: ${({ $active, theme }) => ($active ? theme.shadows.soft : "none")};
  cursor: pointer;
  transition: all 0.3s ease;

  body.dark & {
    background: ${({ $active, theme }) =>
      $active
        ? theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"
        : "transparent"};
    color: ${({ $active, theme }) =>
      $active
        ? theme.colors.dark?.foreground || "#ffffff"
        : theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const SaveBadge = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const PricingGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  max-width: 1400px;
  margin: 0 auto;
  justify-content: center;
`;

const PricingCard = styled.div`
  flex: 1 1 320px;
  min-width: 280px;
  max-width: 460px;
  position: relative;
  padding: 32px;
  border-radius: 24px;
  background: ${({ theme, $popular }) =>
    $popular
      ? theme.colors.light?.card?.background || theme.colors.light?.background || "white"
      : theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: ${({ theme, $popular }) =>
    $popular
      ? `2px solid ${theme.colors.light?.primary || theme.colors.primary.light}`
      : `1px solid ${theme.colors.light?.border || "hsl(240, 10%, 90%)"}`};
  box-shadow: ${({ theme, $popular }) => ($popular ? theme.shadows.glow : theme.shadows.soft)};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme, $popular }) =>
      $popular
        ? theme.colors.dark?.primary || theme.colors.primary.light
        : theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const PlanHeader = styled.div`
  margin-bottom: 24px;
`;

const PlanName = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h3};
  font-weight: 700;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const PlanDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const PriceDisplay = styled.div`
  margin-bottom: 24px;
`;

const PriceAmount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
`;

const PriceValue = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(2.5rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const PricePeriod = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const PriceNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 4px 0 0 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const CheckIcon = styled(Check)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  flex-shrink: 0;
  margin-top: 2px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const CheckCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FAQSection = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const FAQHeader = styled.div`
  max-width: 768px;
  margin: 0 auto 48px;
  text-align: center;
`;

const FAQTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FAQDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FAQList = styled.div`
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FAQItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const FAQQuestion = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: start;
  gap: 12px;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FAQIcon = styled(HelpCircle)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  flex-shrink: 0;
  margin-top: 2px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const FAQAnswer = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 0 32px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const CTASection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2.25rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£" };
const INTERVALS = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "half_year", label: "Half-yearly" },
  { value: "year", label: "Yearly" },
];
const INTERVAL_PERIOD = { month: "month", quarter: "quarter", half_year: "6 months", year: "year" };
const FALLBACK_PLANS = [
  { id: "free_trial", name: "Free Trial", description: "Full access for 14 days", features: ["Full access for 14 days"], prices: null, interval: null },
  { id: "starter", name: "Starter", description: "Up to 2 stores, basic analytics", features: ["Up to 2 stores", "Basic analytics"], prices: { USD: { amount: 2900, month: 2900, quarter: 7500, half_year: 14000, year: 25000 }, EUR: { amount: 2700, month: 2700, quarter: 7000, half_year: 13000, year: 23000 }, GBP: { amount: 2500, month: 2500, quarter: 6500, half_year: 12000, year: 21000 } }, interval: "month" },
  { id: "pro", name: "Pro", description: "Up to 10 stores, advanced analytics", features: ["Up to 10 stores", "Advanced analytics", "Priority support"], prices: { USD: { amount: 7900, month: 7900, quarter: 20500, half_year: 38000, year: 68000 }, EUR: { amount: 7300, month: 7300, quarter: 19000, half_year: 35000, year: 63000 }, GBP: { amount: 6500, month: 6500, quarter: 16900, half_year: 31200, year: 56000 } }, interval: "month" },
  { id: "enterprise", name: "Enterprise", description: "Unlimited stores, dedicated support", features: ["Unlimited stores", "Custom integrations", "Dedicated support"], prices: { USD: { amount: 19900, month: 19900, quarter: 52000, half_year: 96000, year: 172000 }, EUR: { amount: 18500, month: 18500, quarter: 48200, half_year: 89000, year: 160000 }, GBP: { amount: 16500, month: 16500, quarter: 43000, half_year: 79200, year: 142000 } }, interval: "month" },
];

const faqs = [
  {
    question: "Can I change plans later?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and we'll prorate your billing.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards, PayPal, and bank transfers for annual plans. Enterprise customers can also pay by invoice.",
  },
  {
    question: "Is there a free trial?",
    answer: "Yes! All paid plans come with a 14-day free trial. No credit card required to start.",
  },
  {
    question: "What happens when I cancel?",
    answer: "You can cancel anytime. Your data will be available for export for 30 days after cancellation.",
  },
];

function getPriceForInterval(plan, currency, interval = "month") {
  const p = plan.prices?.[currency];
  if (!p) return 0;
  const amt = p[interval] ?? p.month ?? p.amount;
  return amt != null ? amt / 100 : 0;
}

export default function Pricing() {
  const [interval, setInterval] = useState("month");
  const [currency, setCurrency] = useState("USD");
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${apiConfig.BASE_URL}/api/public/plans`).then((res) => {
      setPlans(Array.isArray(res.data) ? res.data : FALLBACK_PLANS);
    }).catch(() => setPlans(FALLBACK_PLANS)).finally(() => setLoading(false));
  }, []);

  const symbol = CURRENCY_SYMBOLS[currency] || currency + " ";
  const isYearly = interval === "year"; // backward compat if any ref remains

  return (
    <Layout>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for teams of all sizes. Start free and scale as you grow with Kliq CRM."
        keywords="pricing, plans, free trial, enterprise, SaaS"
        hreflang
      />
      <PageTransition>
        {/* Hero */}
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Pricing</Badge>
              </BadgeWrapper>
              <Title>
                Simple, transparent <GradientText>pricing</GradientText>
              </Title>
              <Description>
                Choose the perfect plan for your needs. All plans include a 14-day free trial.
              </Description>

              {/* Currency + Billing cycle in same row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", alignItems: "center", marginBottom: 16 }}>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 14, background: "white" }}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
                <BillingToggle>
                  {INTERVALS.map((opt) => (
                    <ToggleButton key={opt.value} $active={interval === opt.value} onClick={() => setInterval(opt.value)}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </BillingToggle>
              </div>
            </HeroContent>
          </Container>
        </Section>

        {/* Pricing Cards */}
        <Section style={{ paddingTop: "32px", paddingBottom: "64px" }}>
          <Container>
            <PricingGrid>
              {loading ? (
                <p style={{ width: "100%", textAlign: "center", color: "#6b7280" }}>Loading plans…</p>
              ) : (
                plans.map((plan) => {
                  const price = getPriceForInterval(plan, currency, interval);
                  const isPopular = plan.id === "pro" || plan.slug === "pro";
                  const cta = plan.id === "free_trial" || plan.slug === "free_trial" ? "Get Started Free" : "Start Free Trial";
                  const periodLabel = INTERVAL_PERIOD[interval] || "month";
                  return (
                <PricingCard key={plan.id || plan.slug || plan.name} $popular={isPopular}>
                  {isPopular && <PopularBadge>Most Popular</PopularBadge>}
                  <PlanHeader>
                    <PlanName>{plan.name}</PlanName>
                    <PlanDescription>{plan.description}</PlanDescription>
                  </PlanHeader>

                  <PriceDisplay>
                    <PriceAmount>
                      <PriceValue>
                        {symbol}{price > 0 ? price.toFixed(2) : "0"}
                      </PriceValue>
                      {price > 0 && (
                        <PricePeriod>/{periodLabel}</PricePeriod>
                      )}
                    </PriceAmount>
                  </PriceDisplay>

                  <Link to="/signup" style={{ textDecoration: "none", display: "block", marginBottom: "32px" }}>
                    <Button variant={isPopular ? "hero" : "outline"} fullWidth size="lg">
                      {cta}
                    </Button>
                  </Link>

                  <FeaturesList>
                    {(plan.features || []).map((feature) => (
                      <FeatureItem key={feature}>
                        <CheckCircle>
                          <CheckIcon size={12} />
                        </CheckCircle>
                        <span>{feature}</span>
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </PricingCard>
              );
                })
              )}
            </PricingGrid>
          </Container>
        </Section>

        {/* FAQ */}
        <FAQSection>
          <Container>
            <FAQHeader>
              <FAQTitle>Frequently asked questions</FAQTitle>
              <FAQDescription>Everything you need to know about our pricing.</FAQDescription>
            </FAQHeader>

            <FAQList>
              {faqs.map((faq) => (
                <FAQItem key={faq.question}>
                  <FAQQuestion>
                    <FAQIcon />
                    {faq.question}
                  </FAQQuestion>
                  <FAQAnswer>{faq.answer}</FAQAnswer>
                </FAQItem>
              ))}
            </FAQList>
          </Container>
        </FAQSection>

        {/* CTA */}
        <CTASection>
          <Container>
            <HeroContent>
              <SectionTitle>Still have questions?</SectionTitle>
              <Description style={{ marginBottom: "32px" }}>
                Our team is here to help you find the perfect plan for your needs.
              </Description>
              <CTAWrapper>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="gradient" size="lg">
                    Talk to Sales
                    <ArrowRight size={20} />
                  </Button>
                </Link>
              </CTAWrapper>
            </HeroContent>
          </Container>
        </CTASection>
      </PageTransition>
    </Layout>
  );
}
